// Vite HMR handler component with Ant Design notifications
import { useEffect, useRef } from 'react';
import { useNotification } from '@context/NotificationProvider';

const ViteHMRHandler = () => {
    const { showNotification } = useNotification();
    const reloadTimeoutRef = useRef(null);
    const hasShownReloadNotification = useRef(false);

    useEffect(() => {
        if (!import.meta.hot || !import.meta.env.DEV) {
            return;
        }

        const handleBeforeFullReload = () => {
            // Clear any existing timeout
            if (reloadTimeoutRef.current) {
                clearTimeout(reloadTimeoutRef.current);
            }

            // Only show notification once per reload cycle
            if (!hasShownReloadNotification.current) {
                hasShownReloadNotification.current = true;

                showNotification(
                    'warning',
                    'Update Available',
                    'Significant changes detected. The page will reload automatically in 5 seconds.',
                    null,
                    0, // Don't auto-close
                    'topRight',
                    () => {
                        window.location.reload();
                    }
                );

                // Auto-reload after 5 seconds
                reloadTimeoutRef.current = setTimeout(() => {
                    window.location.reload();
                }, 5000);
            }
        };

        const handleError = (err) => {
            console.error('HMR Error:', err);
            
            if (reloadTimeoutRef.current) {
                clearTimeout(reloadTimeoutRef.current);
            }

            showNotification(
                'error',
                'Update Failed',
                'Failed to apply hot update. Reloading page in 3 seconds...',
                null,
                6,
                'topRight'
            );

            setTimeout(() => {
                window.location.reload();
            }, 3000);
        };

        const handleAfterUpdate = () => {
            // Reset flag on successful update
            hasShownReloadNotification.current = false;
            
            showNotification(
                'success',
                'Update Applied',
                'Changes have been applied successfully.',
                null,
                6,
                'topRight'
            );
        };

        // Listen to HMR events
        import.meta.hot.on('vite:beforeFullReload', handleBeforeFullReload);
        import.meta.hot.on('vite:error', handleError);
        import.meta.hot.on('vite:afterUpdate', handleAfterUpdate);

        return () => {
            if (import.meta.hot) {
                import.meta.hot.off('vite:beforeFullReload', handleBeforeFullReload);
                import.meta.hot.off('vite:error', handleError);
                import.meta.hot.off('vite:afterUpdate', handleAfterUpdate);
            }
            if (reloadTimeoutRef.current) {
                clearTimeout(reloadTimeoutRef.current);
            }
        };
    }, [showNotification]);

    return null;
};

export default ViteHMRHandler;
